// Record a GAPP decision — or, for one already recorded, tell the family —
// in one panel: the decision as it reads on DCH's letter, the Letter of
// Notification for the family to sign, and the email that tells them.
//
// mode "record": the decision is saved from here (the caller does it).
// mode "notify": the decision is already on file (recorded before this panel
//   existed, or recorded without an email); only the letter date, the LON and
//   the email are new.
//
// Nothing is saved or sent until the button at the bottom. The email is
// reviewed first — address prefilled from the record but editable for this
// send, a send / don't-send tick, and the wording — and its preview is the
// email exactly as it will go out. The LON is the original PDF from Alliant:
// its signature boxes are placed from the words printed on it
// (lib/documents/lonLayout) and can be checked and moved before it goes.

import { useEffect, useMemo, useState } from 'react';
import FieldPlacer from '../signing/FieldPlacer';
import { placeLonFields } from '../../lib/documents/lonLayout';
import { UPLOAD_PARTIES, PARTY } from '../../lib/documents/packets';
import { gappDecisionWording, gappDecisionEmail, isSpanishFamily } from '../../lib/notifications';
import { decisionOutcomes, outcomeGrants, suggestOutcome, reconsiderationDueFrom, LON_RETURN_DAYS } from '../../lib/paCycles';
import { SUPPORT_EMAIL } from '../../lib/org';

const OUTCOME_LABEL = {
  approved: 'Approved', reduced: 'Approved, fewer hours than asked', denied: 'Denied',
  revised: 'Hours revised', upheld: 'Decision upheld',
};
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || '').trim());
const fmt = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const LON_PLACES = { name: 'printed name', agreement: 'agree / disagree', parent: 'parent signature', caregiver: 'primary caregiver signature', nurse: 'agency signature (page 2)' };

export default function DecisionPanel({ mode = 'record', cycle, client, initial, requested, profile, busy, onConfirm, onCancel }) {
  const recon = cycle.kind === 'reconsideration';
  const outcomes = decisionOutcomes(cycle);
  const notify = mode === 'notify';
  const [outcome, setOutcome] = useState(initial.outcome || outcomes[0]);
  const [touched, setTouched] = useState(notify);
  const [dec, setDec] = useState({ auth_number: '', letter_date: '', start_date: '', expiry_date: '', skilled: '', pss: '', reason: '', ...initial.dec });
  const [letter, setLetter] = useState(null);          // { file, bytes, name, fields, missing }
  const [placing, setPlacing] = useState(false);
  const [reading, setReading] = useState(false);
  const [agencyName, setAgencyName] = useState(profile?.full_name || '');
  const guardian = [client.guardian_first_name, client.guardian_last_name].filter(Boolean).join(' ');
  const onFile = (client.guardian_email || '').trim();
  const [mail, setMail] = useState({ send: !notify || !cycle.family_decision_notified_at, to: onFile, lang: isSpanishFamily(client) ? 'es' : 'en', subject: '', opening: '', closing: '', edited: false });

  const grants = outcomeGrants(outcome);
  const hasLetter = grants && !!letter;
  const set = (k) => (e) => setDec((d) => ({ ...d, [k]: e.target.value }));

  // "Fewer hours than asked" follows the hours typed, until someone picks.
  const suggested = suggestOutcome(cycle, { requested, skilled: dec.skilled, pss: dec.pss });
  useEffect(() => { if (!touched && !recon && outcome !== 'denied') setOutcome(suggested); }, [suggested, touched, recon, outcome]);

  // The wording follows the outcome, the letter and the language — until it is edited.
  useEffect(() => {
    if (mail.edited) return;
    const w = gappDecisionWording(client, { kind: cycle.kind, outcome, hasLetter, lang: mail.lang }) || {};
    setMail((m) => ({ ...m, subject: w.subject || '', opening: w.opening || '', closing: w.closing || '' }));
  }, [client, cycle.kind, outcome, hasLetter, mail.lang, mail.edited]);
  const editMail = (k) => (e) => setMail((m) => ({ ...m, [k]: e.target.value, edited: true }));

  const decision = grants
    ? { authNumber: dec.auth_number, skilled: dec.skilled, pss: dec.pss, start: dec.start_date, expiry: dec.expiry_date, letterDate: dec.letter_date }
    : { reason: dec.reason, letterDate: dec.letter_date };
  const preview = useMemo(() => gappDecisionEmail(client, {
    outcome, lang: mail.lang, decision, signUrl: hasLetter ? '#' : null,
    wording: { subject: mail.subject, opening: mail.opening, closing: mail.closing },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [client, outcome, mail.lang, mail.subject, mail.opening, mail.closing, hasLetter, JSON.stringify(decision)]);

  const pickLetter = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    if (!/pdf$/i.test(file.name) && file.type !== 'application/pdf') { setLetter({ error: 'Upload the letter as a PDF.' }); return; }
    setReading(true);
    const bytes = new Uint8Array(await file.arrayBuffer());
    const placed = await placeLonFields(bytes).catch(() => ({ fields: [], missing: Object.keys(LON_PLACES) }));
    setLetter({ file, bytes, name: file.name, fields: placed.fields, missing: placed.missing });
    setReading(false);
    if (placed.missing.length) setPlacing(true);   // couldn't find everything: show it straight away
  };

  // What has to be there before it can go.
  const problems = [];
  if (!dec.letter_date) problems.push('the date on the letter');
  if (!notify && grants && (!dec.start_date || !dec.expiry_date)) problems.push(recon ? 'when the new hours start, and the expiry' : 'the start and expiry dates');
  if (!notify && !grants && !dec.reason.trim()) problems.push(recon ? 'what DCH said' : 'the reason DCH gave');
  if (letter && !letter.error && !letter.fields.some((f) => f.party === PARTY.CLIENT && f.kind === 'sig')) problems.push("a signature box for the family on the letter");
  if (letter && !letter.error && letter.fields.some((f) => f.party === PARTY.AGENCY) && !agencyName.trim()) problems.push('who signs the agency line');
  if (mail.send && !isEmail(mail.to)) problems.push('an email address');
  if (mail.send && !mail.subject.trim()) problems.push('a subject');
  if (notify && !mail.send && !hasLetter) problems.push('an email to send or a letter to save');

  // The button names the address it sends to — a corrected or test address
  // is never mistaken for the family's.
  const toWho = mail.to.trim() || 'the family';
  const go = () => onConfirm({
    outcome, dec, agencyName: agencyName.trim(),
    letter: hasLetter ? { file: letter.file, name: letter.name, fields: letter.fields } : null,
    mail: { send: mail.send, to: mail.to.trim(), lang: mail.lang, wording: { subject: mail.subject.trim(), opening: mail.opening, closing: mail.closing } },
  });
  const label = notify
    ? (mail.send ? `Send to ${toWho}` : 'Save the letter for signature')
    : mail.send ? `Record decision & send to ${toWho}` : 'Record decision (no email)';

  const returnBy = reconsiderationDueFrom(dec.letter_date);   // same 30 days, from the same letter
  return (
    <div style={{ padding: '14px 18px', background: 'var(--bg2)', display: 'grid', gap: 12, fontSize: 12 }}>
      {/* ── the decision ── */}
      <div style={box}>
        <div style={head}>{notify ? 'The decision on file' : 'Outcome'}</div>
        {!notify ? (
          <div role="group" aria-label="Outcome" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {outcomes.map((o) => (
              <button key={o} type="button" aria-pressed={outcome === o} className={`btn btn-sm ${outcome === o ? 'btn-primary' : 'btn-outline'}`} onClick={() => { setOutcome(o); setTouched(true); }}>{OUTCOME_LABEL[o]}</button>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text)' }}>{OUTCOME_LABEL[outcome]}{grants && dec.auth_number ? ` · PA ${dec.auth_number}` : ''}{grants && dec.skilled !== '' && dec.skilled != null ? ` · ${dec.skilled} h/wk skilled` : ''}{grants && dec.pss !== '' && dec.pss != null ? ` · ${dec.pss} h PSS` : ''}{grants && dec.expiry_date ? ` · ${fmt(dec.start_date)} – ${fmt(dec.expiry_date)}` : ''}{!grants && dec.reason ? ` · ${dec.reason}` : ''}</div>
        )}
        {notify && grants && !recon && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', color: 'var(--text3)' }}>Tell the family it was
            {['approved', 'reduced'].map((o) => <button key={o} type="button" className={`btn btn-sm ${outcome === o ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setOutcome(o)}>{OUTCOME_LABEL[o].toLowerCase()}</button>)}
          </div>
        )}
        {!notify && !recon && (
          <div style={hint}>{requested ? `The assessment asked for ${[requested.skilled != null ? `${requested.skilled} h/wk skilled` : null, requested.unskilled != null ? `${requested.unskilled} h/wk PSS` : null].filter(Boolean).join(' + ')}, so CAERA suggests the outcome from the hours you type.` : 'The nurse assessment has no requested hours recorded, so pick the outcome yourself.'}</div>
        )}
        <div style={grid}>
          {!notify && grants && <label>PA number<input className="form-input" value={dec.auth_number} onChange={set('auth_number')} placeholder="from the letter" /></label>}
          <label>Date on the letter<input className="form-input" type="date" value={dec.letter_date} onChange={set('letter_date')} /></label>
          {!notify && grants && <>
            <label>{recon ? 'New hours start' : 'Effective'}<input className="form-input" type="date" value={dec.start_date} onChange={set('start_date')} /></label>
            <label>{recon ? 'Expires (unchanged)' : 'Through'}<input className="form-input" type="date" value={dec.expiry_date} onChange={set('expiry_date')} /></label>
            <label>Skilled h/wk<input className="form-input" type="number" step="0.5" value={dec.skilled} onChange={set('skilled')} /></label>
            <label>PSS h/wk<input className="form-input" type="number" step="0.5" value={dec.pss} onChange={set('pss')} /></label>
          </>}
          {!notify && <label style={{ gridColumn: '1 / -1' }}>{grants ? 'Note (optional)' : recon ? 'What DCH said' : 'Reason DCH gave'}<input className="form-input" value={dec.reason} onChange={set('reason')} placeholder={grants ? '' : 'In their words where you have them.'} /></label>}
        </div>
        <div style={hint}>"Date on the letter" is the Date of Determination. The {LON_RETURN_DAYS} days to return the signed letter to Alliant run from it{['reduced', 'denied'].includes(outcome) ? ", and so does the family's right to ask for a reconsideration" : ''}{returnBy ? ` — to ${fmt(returnBy)}` : ''}.</div>
      </div>

      {/* ── the Letter of Notification ── */}
      {grants && (
        <div style={box}>
          <div style={head}>Letter of Notification for {guardian || 'the family'} to sign</div>
          {!letter || letter.error ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>{reading ? 'Reading the letter…' : 'Upload the letter (PDF)'}<input type="file" accept="application/pdf,.pdf" onChange={pickLetter} style={{ display: 'none' }} disabled={reading} /></label>
              <span style={hint}>{letter?.error || 'The PDF from Alliant, as it came. No letter yet? Record the decision anyway; the letter step stays open until you add it.'}</span>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--card-bg)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{letter.name}</div>
                  <div style={{ ...hint, color: letter.missing.length ? 'var(--warn)' : 'var(--text3)' }}>
                    {letter.missing.length
                      ? `Couldn't find ${letter.missing.map((k) => LON_PLACES[k]).join(', ')} on this letter. Check placement and add what's missing.`
                      : `${letter.fields.length} boxes placed from the letter: name, agree / disagree, both family signatures and dates, and the agency line.`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => setPlacing(true)}>Check placement</button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setLetter(null)}>Remove</button>
                </div>
              </div>
              <div style={{ display: 'grid', gap: 8, marginTop: 4 }}>
                <div style={signer}><span style={hint}>Parent and primary caregiver</span><span><b>{guardian || 'The family'}</b> · by the link in the email below. They tick <i>agree</i> or <i>disagree</i>, then sign once for both lines.</span></div>
                {letter.fields.some((f) => f.party === PARTY.AGENCY) && (
                  <div style={signer}><span style={hint}>Nursing agency line (page 2)</span>
                    <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}><input className="form-input" style={{ maxWidth: 240 }} value={agencyName} onChange={(e) => setAgencyName(e.target.value)} aria-label="Who signs the agency line" /><span style={hint}>Anyone in the office or a nurse. Signed in CAERA from the next step.</span></span></div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── the email ── */}
      <div style={box}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, fontWeight: 600, color: 'var(--text)', cursor: 'pointer' }}>
          <input type="checkbox" checked={mail.send} onChange={(e) => setMail((m) => ({ ...m, send: e.target.checked }))} style={{ width: 'auto' }} />
          Email {guardian || 'the family'}{notify && cycle.family_decision_notified_at ? ` (already emailed ${fmt(cycle.family_decision_notified_at)})` : ''}
        </label>
        {mail.send && (
          <>
            <div style={grid}>
              <label style={{ gridColumn: 'span 2' }}>To<input className="form-input" type="email" value={mail.to} onChange={(e) => setMail((m) => ({ ...m, to: e.target.value }))} placeholder="family email" />
                <span style={hint}>{onFile ? (mail.to.trim().toLowerCase() === onFile.toLowerCase() ? 'From the client record.' : `Changed for this email only. The record still says ${onFile}.`) : 'No email on the client record. Type one for this email.'}</span></label>
              <label>Language<select className="form-select" value={mail.lang} onChange={(e) => setMail((m) => ({ ...m, lang: e.target.value }))}><option value="en">English</option><option value="es">Spanish</option></select></label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
              <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
                <label>Subject<input className="form-input" value={mail.subject} onChange={editMail('subject')} /></label>
                <label>Opening<textarea className="form-input" rows={3} value={mail.opening} onChange={editMail('opening')} /></label>
                <label>Closing<textarea className="form-input" rows={5} value={mail.closing} onChange={editMail('closing')} /></label>
                <div style={hint}>The box with the hours and dates{['reduced', 'denied'].includes(outcome) ? ', the reconsideration deadline' : ''}{hasLetter ? ' and the signing button' : ''} are filled in by CAERA from the decision. You edit the words around them.{mail.edited ? <> <button type="button" onClick={() => setMail((m) => ({ ...m, edited: false }))} style={linkBtn}>Reset wording</button></> : null}</div>
              </div>
              <div>
                <div style={{ ...hint, marginBottom: 4 }}>Preview · copied to {SUPPORT_EMAIL}</div>
                <iframe title="Email preview" sandbox="allow-same-origin" srcDoc={inertLinks(preview.html)} style={{ width: '100%', height: 560, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg2)' }} />
              </div>
            </div>
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-primary btn-sm" disabled={!!busy || problems.length > 0} onClick={go}>{busy ? 'Working…' : label}</button>
        <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={!!busy}>Cancel</button>
        {problems.length > 0 && <span style={{ color: 'var(--text3)' }}>Still needs {problems.join(', ')}.</span>}
      </div>

      {placing && letter?.bytes && (
        <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && setPlacing(false)}>
          <div className="modal" style={{ maxWidth: 1000, width: '96vw' }} onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Signature boxes — {letter.name}</div><button className="btn btn-ghost btn-sm" onClick={() => setPlacing(false)}>✕</button></div>
            <div className="modal-body" style={{ maxHeight: '75vh', overflow: 'auto' }}>
              <FieldPlacer bytes={letter.bytes} fields={letter.fields} onChange={(fields) => setLetter((l) => ({ ...l, fields }))} parties={UPLOAD_PARTIES} defaultParty={PARTY.CLIENT} />
            </div>
            <div className="modal-footer"><button className="btn btn-primary" onClick={() => setPlacing(false)}>Done</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// The preview is for reading: a click on its signing button (href "#", which
// resolves against the CRM page) or its footer link used to send the frame to
// that page, where — scripts being blocked in the frame — it showed CAERA's
// "You need to enable JavaScript" instead of the email (Kristy, 2026-10-05).
const inertLinks = (html) => html.replace('</head>', '<style>a{pointer-events:none;cursor:default}</style></head>');

const box = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, display: 'grid', gap: 10 };
const head = { fontSize: 13.5, fontWeight: 700, color: 'var(--text)' };
const hint = { fontSize: 11.5, color: 'var(--text3)' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px 10px', alignItems: 'end' };
const signer = { display: 'grid', gridTemplateColumns: 'minmax(130px, 180px) 1fr', gap: 10, alignItems: 'center', fontSize: 12.5 };
const linkBtn = { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 'inherit', color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' };
